/**
 * Pre-fetch Share URLs Hook
 *
 * Triggers pre-fetching of all platform-specific short URLs when the share
 * sheet opens. Uses React Query for caching and deduplication.
 *
 * This eliminates async delay during click handlers, solving Safari's
 * popup blocker issues by providing synchronous cache access.
 *
 * @example
 * const { getUrlForPlatform, isReady } = usePrefetchShareUrls(
 *   open ? context : null
 * );
 *
 * // In click handler - sync access to pre-fetched URL
 * const handleShare = (platform: SocialPlatform) => {
 *   const prefetchedUrl = isReady ? getUrlForPlatform(platform) : undefined;
 *   shareToSocialPlatform(platform, context, prefetchedUrl);
 * };
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  fetchAllPlatformUrls,
  getEntityId,
  type PlatformUrlsResult,
  type PrefetchResult,
} from '@lib/shortUrlCache';
import { getShareUrl, type ShareUrlOptions } from '@utils/shareHelpers';
import { logger } from '@utils/logger';
import { SocialPlatform, type ShareContext, ShareEntityType } from '@utils/shareConfig';

interface UsePrefetchShareUrlsResult {
  /**
   * Synchronously get the pre-fetched URL for a platform.
   * Returns undefined if not ready, allowing fallback to async fetch.
   */
  getUrlForPlatform: (platform: SocialPlatform) => string | undefined;

  /**
   * Whether pre-fetching is currently in progress
   */
  isPrefetching: boolean;

  /**
   * Whether pre-fetching is complete and URLs are ready for use
   */
  isReady: boolean;
}

/**
 * Build query key for platform URLs
 */
function getPlatformUrlsQueryKey(context: ShareContext) {
  return ['platformUrls', context.path, context.sharerId, getEntityId(context)] as const;
}

/**
 * Build ShareUrlOptions for a specific platform from context
 */
function buildOptionsForPlatform(context: ShareContext, platform: SocialPlatform): ShareUrlOptions {
  const base: ShareUrlOptions = {
    sharerId: context.sharerId,
    platform,
  };

  switch (context.entityType) {
    case ShareEntityType.Event:
      return { ...base, eventId: context.eventId };
    case ShareEntityType.Question:
      return { ...base, questionId: context.questionId };
    case ShareEntityType.Profile:
      return { ...base, profileId: context.profileId };
    case ShareEntityType.Community:
    case ShareEntityType.App:
      return base;
  }
}

/**
 * Hook to pre-fetch all platform-specific short URLs when share sheet opens
 *
 * Uses React Query for:
 * - Automatic caching (staleTime: Infinity for stable short URLs)
 * - Request deduplication across components
 * - Race condition handling
 *
 * @param context - The share context when open, or null when closed
 * @returns Object with getUrlForPlatform function and status flags
 */
export function usePrefetchShareUrls(context: ShareContext | null): UsePrefetchShareUrlsResult {
  const queryClient = useQueryClient();

  // Fetch all platform URLs when context is available
  const { isFetching, isSuccess } = useQuery({
    queryKey: context ? getPlatformUrlsQueryKey(context) : ['platformUrls', 'disabled'],
    queryFn: () => {
      if (!context) throw new Error('Context is required');
      return fetchAllPlatformUrls(context);
    },
    enabled: !!context,
    staleTime: Infinity, // Short URLs are stable - never refetch
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  // Synchronous getter that reads directly from React Query cache
  // This is critical for Safari popup timing - must be sync
  const getUrlForPlatform = useCallback(
    (platform: SocialPlatform): string | undefined => {
      if (!context) return undefined;

      // Read from React Query cache synchronously
      const cached = queryClient.getQueryData<PlatformUrlsResult>(getPlatformUrlsQueryKey(context));

      if (cached) {
        const result = cached.find((r: PrefetchResult) => r.platform === platform);
        if (result) {
          logger.debug('[Share] Cache HIT - using prefetched short URL', {
            platform,
            url: result.url,
            source: 'react-query-cache',
          });
          return result.url;
        }
      }

      // Fallback to full URL if not in cache
      // This ensures we always have something to share
      const options = buildOptionsForPlatform(context, platform);
      const fallbackUrl = getShareUrl(context.path, options);
      logger.warn('[Share] Cache MISS - using full URL fallback', {
        platform,
        url: fallbackUrl,
        source: 'full-url-fallback',
        cacheExists: !!cached,
      });
      return fallbackUrl;
    },
    [context, queryClient],
  );

  return {
    getUrlForPlatform,
    isPrefetching: isFetching,
    isReady: isSuccess,
  };
}
